import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Sell } from '../../common/types/venda';
import { Checkout } from '../../common/types/checkout';
import { CreatePurchaseContract } from '../../common/types/create-purchase-contract';

import { useWebSocket } from '../../common/hooks/useWebSocket';
import { usePixRedirects } from '../../common/hooks/usePixRedirects';
import { useVendaStatusObserver } from '../../common/hooks/useVendaStatusObserver';

import {
  startTimer,
  tick,
  TimerState,
} from '../../common/redux/slices/pix-timer.slice';

import { navigateExternalNoReferrer } from '../../common/utils/navigate-no-referer';
import { emitFacebookEvent } from '../../common/utils/emit-facebook-event';

import { PixPaymentPage } from '../pages/pix/pix-payment.page';
import { PixPaymentPageSkeleton } from '../molecules/pix-payment-skeleton/pix-payment-skeleton';
import { awaitEmitUtmifyEvent } from '../../common/utils/emitUtmifyEvent';

export const PixTemplate = () => {
  const dispatch = useDispatch();
  const { socket } = useWebSocket();
  const navigate = useNavigate();
  const location = useLocation();

  const [venda, setVenda] = useState<Sell>({} as Sell);
  const [checkout, setCheckout] = useState<Checkout>({} as Checkout);
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const isRunning = useSelector(
    (state: { timer: TimerState }) => state.timer.isRunning,
  );
  const seconds = useSelector(
    (state: { timer: TimerState }) => state.timer.seconds,
  );

  useEffect(() => {
    if (!location?.state) {
      window.location.href = 'https://vendepay.com.br';
    } else {
      setVenda(location?.state?.purchase?.data);
      setCheckout(location?.state?.checkout);
      setIsLoading(false);
    }
    setVenda(location?.state?.purchase?.data);
    setCheckout(location?.state?.checkout);
  }, [location.state]);

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(venda?.pixCopiaCola ?? '');
    setShowTooltip(true);
    setTimeout(() => setShowTooltip(false), 4000);
  };

  const buildParams = useCallback(() => {
    const params = new URLSearchParams(location.search);

    if (venda?.id) {
      params.set('vendaId', venda.id);
      params.set('name', venda.nomeComprador);
      params.set('email', venda.emailComprador);
      params.set('phone', venda.telefoneComprador);
      params.set('productId', venda.produtoId);
    }

    return params;
  }, [
    location.search,
    venda?.id,
    venda?.nomeComprador,
    venda?.emailComprador,
    venda?.telefoneComprador,
    venda?.produtoId,
  ]);

  const handleSuccess = useCallback(() => {
    const params = buildParams();

    if (!checkout?.produto?.purchaseNoPix) {
      emitFacebookEvent({
        event: 'Purchase',
        checkout,
        data: {
          email: venda?.emailComprador,
          fullName: `${venda?.nomeComprador} ${venda?.sobrenomeComprador}`,
          phoneNumber: venda?.telefoneComprador,
        } as CreatePurchaseContract,
      });
    }

    if (typeof window !== 'undefined' && (window as any)._uxa) {
      try {
        (window as any)._uxa.push([
          'track',
          'checkout_completed',
          { paymentStatus: 'success' },
        ]);
      } catch (e) {
        console.warn('[Contentsquare] Erro ao emitir evento de conclusão:', e);
      }
    }

    void (async () => {
      if (venda?.id) {
        await awaitEmitUtmifyEvent({ event: 'paid', vendaId: venda.id });
      }
      if (checkout?.paginaObrigado && checkout?.paginaObrigado?.length > 0) {
        navigateExternalNoReferrer(checkout?.paginaObrigado, {
          newTab: false,
          extraParams: params,
        });
      } else {
        navigate('/venda/obrigado', { state: venda });
      }
    })();
  }, [
    buildParams,
    checkout,
    navigate,
    venda,
  ]);

  const handleFailed = useCallback(() => {
    const params = buildParams();

    if (typeof window !== 'undefined' && (window as any)._uxa) {
      try {
        (window as any)._uxa.push([
          'track',
          'checkout_completed',
          { paymentStatus: 'failed' },
        ]);
      } catch (e) {
        console.warn('[Contentsquare] Erro ao emitir evento de falha:', e);
      }
    }

    void (async () => {
      if (venda?.id) {
        await awaitEmitUtmifyEvent({ event: 'refused', vendaId: venda.id });
      }
      if (checkout?.pagamentoRecusado && checkout?.pagamentoRecusado?.length > 0) {
        navigateExternalNoReferrer(checkout?.pagamentoRecusado, {
          newTab: false,
          extraParams: params,
        });
      } else {
        navigate(`/venda/compra-recusada/${venda.id}`, {
          state: { purchase: venda },
        });
      }
    })();
  }, [
    buildParams,
    checkout?.pagamentoRecusado,
    navigate,
    venda,
  ]);

  useVendaStatusObserver({
    socket,
    vendaId: venda?.id,
    vendaToken: venda?.vendaToken,

    wsEventName: 'venda-cc',

    fallbackEnabled: true,
    fallbackStartAfterMs: 10000,
    fallbackOnSocketError: true,
    fallbackOnDisconnect: false,

    pollIntervalMs: 2500,
    pollStopAfterMs: 10 * 60_000,

    resolveFromStatus: (status: number) => {
      if (status === 2) return 'success';
      if (status === 3) return 'failed';
      return null;
    },

    onResolved: ({ type }) => {
      if (type === 'success') handleSuccess();
      if (type === 'failed') handleFailed();
    },

    debug: false,
  });

  usePixRedirects({
    socket,
    venda,
    successUrlExterna: checkout?.paginaObrigado,
    failedUrlExterna: checkout?.pagamentoRecusado,
    newTabExternos: false,
  });

  // timer
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isRunning) {
      interval = setInterval(() => {
        dispatch(tick());
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [isRunning, dispatch]);

  const formatTime = (secs: number) => {
    const min = String(Math.floor(secs / 60)).padStart(2, '0');
    const sec = String(secs % 60).padStart(2, '0');
    return `${min}:${sec}`;
  };

  useEffect(() => {
    dispatch(startTimer());
  }, [dispatch]);

  if (isLoading) {
    return <PixPaymentPageSkeleton />;
  }

  return (
    <PixPaymentPage
      pix={venda}
      onCopy={copyToClipboard}
      showTooltip={showTooltip}
      timer={formatTime(seconds)}
    />
  );
};
